import { ExpandLessOutlined, ExpandMoreOutlined } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";

const DetailSectionHeader = ({
  title,
  count,
  collapsible = false,
  expanded = true,
  onToggle,
  fontWeight = 400,
  actions,
  sx = {},
  titleSx,
  fontSize = 12,
  icon,
}) => {
  const handleClick = () => {
    if (collapsible && onToggle) {
      onToggle();
    }
  };

  const handleKeyDown = (e) => {
    if (collapsible && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onToggle?.();
    }
  };

  return (
    <Box
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={collapsible ? "button" : undefined}
      aria-expanded={collapsible ? expanded : undefined}
      tabIndex={collapsible ? 0 : undefined}
      className="detail-section-header"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "4px 4px 0 0",
        minHeight: "36px",
        cursor: collapsible ? "pointer" : "default",
        userSelect: "none",
        color: "#000000ad",
        transition: "background-color 0.2s ease",
        ".MuiSvgIcon-root": { fontSize: `${fontSize + 2}px` },

        "&:focus-visible": {
          outline: "2px solid #3b82f6",
          outlineOffset: "2px",
        },
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.8,
          flex: 1,
          minWidth: 0,
        }}
      >
        <Flx ac gap={fontSize * 0.05}>
          {icon}
          <Txt
            className="subsection-header-title"
            sx={{
              fontWeight: fontWeight,
              fontSize: `${fontSize}px`,
              ...titleSx,
            }}
          >
            {title}
            {count !== undefined && (
              <Typography
                component="span"
                sx={{
                  fontWeight: 500,
                  color: "#374151",
                  ml: 0.5,
                }}
              >
                ({count})
              </Typography>
            )}
          </Txt>
        </Flx>

        {collapsible && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#374151",
            }}
          >
            {expanded ? (
              <ExpandLessOutlined
                className="thin"
                sx={{ fontSize: `${fontSize + 8}px !important` }}
              />
            ) : (
              <ExpandMoreOutlined
                className="thin"
                sx={{ fontSize: `${fontSize + 8}px !important` }}
              />
            )}
          </Box>
        )}
      </Box>
      {actions && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

DetailSectionHeader.propTypes = {
  /** The title text to display in the header */
  title: PropTypes.string.isRequired,
  /** Optional count to display after the title */
  count: PropTypes.number,
  /** Whether the section can be collapsed/expanded */
  collapsible: PropTypes.bool,
  /** Current expanded state */
  expanded: PropTypes.bool,
  /** Callback when toggle is clicked */
  onToggle: PropTypes.func,
  /** Optional action buttons or elements to show in the header */
  actions: PropTypes.node,
  /** Optional MUI sx prop for custom styling */
  sx: PropTypes.object,
};

export default DetailSectionHeader;
